/**
 * Plugin Loader — core primitives for dynamically loading plugin UI modules.
 *
 * This module provides:
 *   - `findPluginUI()` — detect a PluginUI-shaped export in a module (SINGLE SOURCE OF TRUTH)
 *   - `importModuleFromSource()` — load JS source text as an ES module via Blob URL
 *   - `loadBundledPlugins()` — load Vite glob-imported plugin modules
 *
 * All higher-level loaders (gateway-loader, local-loader, built-in) use these primitives.
 */
import type { PluginUI } from "@magic-md/plugin-sdk";
import { frontendLog } from "../utils/frontend-logger";
import { registerPluginUIs } from "./plugin-ui-registry";

// ── PluginUI detection (single source of truth) ──

interface PluginModule {
    [key: string]: unknown;
}

function isPluginUI(obj: unknown): obj is PluginUI {
    if (obj == null || typeof obj !== "object") return false;
    const p = obj as Record<string, unknown>;
    return (
        "manifest" in p &&
        "Renderer" in p &&
        p.manifest != null &&
        typeof p.manifest === "object" &&
        "name" in (p.manifest as Record<string, unknown>) &&
        typeof (p.manifest as Record<string, unknown>).name === "string"
    );
}

/**
 * Detect a PluginUI-shaped export in a dynamically imported module.
 * Checks `default` export first, then all named exports.
 */
export function findPluginUI(mod: Record<string, unknown>): PluginUI | null {
    if (isPluginUI(mod.default)) return mod.default as PluginUI;
    for (const val of Object.values(mod)) {
        if (isPluginUI(val)) return val as PluginUI;
    }
    return null;
}

// ── Blob URL module import ──

/**
 * Import a plugin UI module from JavaScript source text.
 *
 * Creates a same-origin Blob URL and uses `import()` to load the module.
 * This is the universal mechanism for all plugin loading in Tauri apps:
 *   - `asset://` URLs don't set correct MIME for ES modules
 *   - Cross-origin `http://` URLs are blocked in WKWebView
 *   - Blob URLs are always same-origin and work everywhere
 *
 * @param source — The JavaScript source text of the plugin UI bundle
 * @param pluginName — For error messages
 * @returns The PluginUI object found in the module
 * @throws If no PluginUI export is found
 */
export async function importModuleFromSource(
    source: string,
    pluginName: string,
): Promise<PluginUI> {
    const blob = new Blob([source], { type: "application/javascript" });
    const blobUrl = URL.createObjectURL(blob);
    try {
        const mod = (await import(/* @vite-ignore */ blobUrl)) as Record<string, unknown>;
        const pluginUI = findPluginUI(mod);
        if (!pluginUI) {
            throw new Error(`No PluginUI export found in ${pluginName}/dist/ui.js`);
        }
        return pluginUI;
    } finally {
        URL.revokeObjectURL(blobUrl);
    }
}

/** Attach an external stylesheet to a PluginUI without touching document CSS. */
export function withPluginStyles(
    pluginUI: PluginUI,
    cssText: string | null | undefined,
): PluginUI {
    if (!cssText?.trim()) return pluginUI;
    return {
        ...pluginUI,
        styles: [...(pluginUI.styles ?? []), cssText],
    };
}

// ── Bundled plugin loading (Vite import.meta.glob) ──

/**
 * Load plugins from Vite glob imports (DEV mode / code-split bundles).
 *
 * Usage in app entry:
 * ```ts
 * const modules = import.meta.glob('../../../plugins/* /src/ui.ts');
 * await loadBundledPlugins(modules);
 * ```
 */
export async function loadBundledPlugins(
    modules: Record<string, () => Promise<unknown>>,
): Promise<{ loaded: string[]; failed: string[] }> {
    const loaded: string[] = [];
    const failed: string[] = [];

    const entries = Object.entries(modules);
    const results = await Promise.allSettled(
        entries.map(async ([path, importFn]) => {
            const raw = await importFn();
            const mod = (raw != null && typeof raw === "object" ? raw : {}) as PluginModule;
            const pluginUI = findPluginUI(mod);
            if (!pluginUI) {
                throw new Error(`No PluginUI export found in ${path}`);
            }
            return pluginUI;
        }),
    );

    const loadedPlugins: PluginUI[] = [];
    for (const [i, result] of results.entries()) {
        if (result.status === "fulfilled") {
            loadedPlugins.push(result.value);
            loaded.push(result.value.manifest.name);
        } else {
            const path = entries[i][0];
            failed.push(path);
            frontendLog.error(`[plugin-loader] Failed to load ${path}`, result.reason, { source: "plugin" });
        }
    }
    registerPluginUIs(loadedPlugins);

    return { loaded, failed };
}
